import styled from "styled-components";
import { QUERIES, SPACING } from "../../../utils/constants";
import { TransactionTone } from "../types";

export const TransactionList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

export const TransactionsEmpty = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${SPACING.xs}px;
  padding: ${SPACING.xl}px ${SPACING.md}px;
  text-align: center;
  color: var(--color-text-secondary);
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-sm);
`;

export const TransactionItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm + SPACING.xs}px;
  padding: ${SPACING.sm + SPACING.xxs}px ${SPACING.sm}px;
  margin: 0 -${SPACING.sm}px;
  cursor: pointer;
  transition: background 0.15s ease;
  border-radius: 6px;

  &:not(:last-child) {
    border-bottom: 1px solid var(--color-border);
  }

  &:hover {
    background: var(--hover-background);
  }
`;

export const CategoryBadge = styled.div<{ $color: string }>`
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: ${({ $color }) => `${$color}1a`};
  border: 1.5px solid ${({ $color }) => `${$color}44`};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: 0.04em;
  color: ${({ $color }) => $color};
`;

export const TransactionInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const TransactionTitle = styled.span`
  display: block;
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--color-text-default);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
`;

export const TransactionCategory = styled.span`
  display: block;
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-xs);
  font-weight: 400;
  letter-spacing: 0.015em;
  color: var(--color-text-secondary);
  margin-top: ${SPACING.xxs}px;
`;

export const TransactionMeta = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${SPACING.xs}px;
`;

const TONE_COLORS = {
  positive: "var(--color-success)",
  negative: "var(--color-error)",
  neutral: "var(--color-text-default)",
} as const;

export const TransactionAmount = styled.span<{ $tone: TransactionTone }>`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-base);
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ $tone }) => TONE_COLORS[$tone]};
  white-space: nowrap;
`;

export const TransactionDate = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-xs);
  font-weight: 400;
  letter-spacing: 0.025em;
  color: var(--color-text-muted);
`;

export const PlanMetricItem = styled.article`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${SPACING.xs}px;
  min-width: 0;
  overflow-wrap: anywhere;

  &:nth-child(even) {
    padding-left: ${SPACING.sm + SPACING.xs}px;
    border-left: 1px solid var(--color-border);
  }

  @media ${QUERIES.laptopAndUp} {
    &:not(:first-child) {
      padding-left: ${SPACING.lg}px;
      border-left: 1px solid var(--color-border);
    }
  }
`;

export const PlanTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.xs}px;
  min-width: 0;
`;

export const PlanTitle = styled.h4`
  margin: 0;
  font-size: var(--text-base);
  font-weight: 600;
  line-height: 1.4;
  color: var(--color-text-default);
`;

export const PlanMetricValue = styled.div`
  font-size: var(--text-xxxl);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
  color: var(--color-text-default);
`;

export const PlanMetricUnit = styled.span`
  font-size: var(--text-sm);
  font-weight: 400;
`;

export const PlanUnitLine = styled.span`
  font-size: var(--text-sm);
  line-height: 1.4;
  color: var(--color-text-secondary);
`;

export const PlanTargetRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${SPACING.xs}px;
  margin-top: ${SPACING.xs}px;
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
`;

export const PlanTargetDetail = styled.span<{ $color: string }>`
  font-weight: 600;
  color: ${({ $color }) => $color};
`;

export const PlanBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${SPACING.xs}px ${SPACING.sm}px;
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
`;

export const PlanGoalsBlock = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
  padding-top: ${SPACING.lg}px;
  border-top: 1px solid var(--color-border);
`;

export const PlanGoalsTitle = styled.h4`
  margin: 0;
  font-size: var(--text-lg);
  font-weight: 600;
  color: var(--color-text-default);
`;

export const PlanGoalsList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${SPACING.lg}px ${SPACING.xl}px;

  @media ${QUERIES.tabletAndUp} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const PlanGoalRow = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
  min-width: 0;
  font-size: var(--text-sm);
  overflow-wrap: anywhere;
`;

export const PlanGoalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${SPACING.xs}px ${SPACING.sm}px;
`;

export const PlanGoalName = styled.h5`
  margin: 0;
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--color-text-default);
  overflow-wrap: anywhere;
`;

export const PlanGoalMeta = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${SPACING.xs}px ${SPACING.sm}px;
  color: var(--color-text-secondary);
  font-variant-numeric: tabular-nums;
`;

export const PlanGoalMessage = styled.p`
  margin: 0;
  color: var(--color-text-secondary);
`;

export const PlanJustEndedList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

export const PlanJustEndedRow = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${SPACING.sm}px;
  padding: ${SPACING.sm}px 0;
  border-top: 1px solid var(--color-border);
  font-size: var(--text-sm);
  overflow-wrap: anywhere;
`;

export const PlanJustEndedInfo = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACING.xs}px ${SPACING.sm}px;
  min-width: 0;
`;

export const PlanEmptyGoals = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACING.xs}px ${SPACING.sm}px;
  color: var(--color-text-secondary);
  font-size: var(--text-sm);
`;
